import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  VStack,
  Text,
  Spinner,
  Circle,
  Link,
  Button,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { BookingFormData, ListaAttesaDetails } from "../types/Booking";
import { Evento } from "../types/Event";
import BookingModal from "../components/BookingModal";
import { getUserInfo, ClientPrincipal } from "../authConfig";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const EditWaitingList: React.FC = () => {
  const { listaAttesaId } = useParams<{ listaAttesaId: string }>();
  const navigate = useNavigate();
  const [listaAttesa, setListaAttesa] = useState<ListaAttesaDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const userInfo = await getUserInfo();
      setUser(userInfo);
      setAuthChecked(true);
    };
    checkAuth();
  }, []);

  const [searchParams] = useSearchParams();
  const adminEventoId = searchParams.get("from") === "admin" ? searchParams.get("eventoId") : null;
  const isAdminEdit = !!adminEventoId && !!user;
  const backPath = isAdminEdit ? `/eventi/${adminEventoId}/prenotazioni` : user ? "/le-mie-prenotazioni" : "/";

  useEffect(() => {
    const fetchListaAttesa = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/lista-attesa/${listaAttesaId}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Iscrizione alla lista d'attesa non trovata");
          } else {
            setError("Errore nel recupero dei dati");
          }
          return;
        }
        const data: ListaAttesaDetails = await response.json();
        setListaAttesa(data);
      } catch (err) {
        setError("Errore di connessione");
      } finally {
        setLoading(false);
      }
    };

    fetchListaAttesa();
  }, [listaAttesaId]);

  // Past-event restriction applies to customers only
  const blockedError = !isAdminEdit && listaAttesa && new Date(listaAttesa.DataEvento) < new Date()
    ? "Non è possibile modificare un'iscrizione per un evento già passato"
    : null;
  const shownError = error || blockedError;

  if (loading || (adminEventoId && !user && !authChecked)) {
    return (
      <>
        <Box position="fixed" top={0} left={0} right={0} bottom={0} backgroundImage="url('/tavolata.jpg')" backgroundSize="cover" backgroundPosition="center" zIndex={-2} />
        <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.800" zIndex={-1} />
        <Container maxW="container.md" py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" color="white" thickness="4px" />
            <Text color="white">Caricamento...</Text>
          </VStack>
        </Container>
      </>
    );
  }

  if (shownError) {
    return (
      <>
        <Box position="fixed" top={0} left={0} right={0} bottom={0} backgroundImage="url('/tavolata.jpg')" backgroundSize="cover" backgroundPosition="center" zIndex={-2} />
        <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.800" zIndex={-1} />
        <Container maxW="container.md" py={20}>
          <Box bg="blackAlpha.500" backdropFilter="blur(8px)" border="1px solid" borderColor="whiteAlpha.200" p={10} borderRadius="none" textAlign="center">
            <Circle size="80px" bg="red.500" color="white" mx="auto" mb={6}>
              <CloseIcon boxSize={8} />
            </Circle>
            <Text fontSize="xl" fontWeight="500" mb={4} color="white" textTransform="uppercase" letterSpacing="wide">Errore</Text>
            <Text color="gray.400" mb={6}>{shownError}</Text>
            <Link as={RouterLink} to={isAdminEdit ? backPath : "/"}>
              <Button bg="transparent" color="white" border="1px solid" borderColor="whiteAlpha.300" borderRadius="none" _hover={{ bg: "white", color: "gray.900" }}>{isAdminEdit ? "Torna all'evento" : "Torna alla home"}</Button>
            </Link>
          </Box>
        </Container>
      </>
    );
  }

  if (!listaAttesa) {
    return null;
  }

  // Build pseudo Evento object from lista attesa data
  const evento: Evento = {
    EventoId: listaAttesa.EventoId,
    Titolo: listaAttesa.EventoTitolo,
    DataEvento: listaAttesa.DataEvento,
    PrezzoAlcolico: listaAttesa.PrezzoAlcolico,
    PrezzoAnalcolico: listaAttesa.PrezzoAnalcolico,
    Ingredienti: listaAttesa.Ingredienti || "",
    PostiTotali: listaAttesa.PostiTotali || 0,
    PostiOccupati: listaAttesa.PostiOccupati || 0,
    Menu: "",
    Attivo: true,
  };

  // Parse participant names
  let nomiPartecipanti: string[] = [];
  if (listaAttesa.NomiPartecipanti) {
    try {
      nomiPartecipanti = JSON.parse(listaAttesa.NomiPartecipanti);
    } catch {
      nomiPartecipanti = [];
    }
  }

  // Build initial form data from lista attesa
  const initialData: BookingFormData = {
    Nome: listaAttesa.Nome,
    Cognome: listaAttesa.Cognome,
    Email: listaAttesa.Email,
    Telefono: listaAttesa.Telefono || "",
    TipoSoggetto: listaAttesa.TipoSoggetto || "Privato",
    CodiceFiscale: listaAttesa.CodiceFiscale || "",
    PartitaIVA: listaAttesa.PartitaIVA || "",
    RagioneSociale: listaAttesa.RagioneSociale || "",
    Indirizzo: listaAttesa.Indirizzo || "",
    CAP: listaAttesa.CAP || "",
    Citta: listaAttesa.Citta || "",
    Provincia: listaAttesa.Provincia || "",
    Nazione: listaAttesa.Nazione || "Italia",
    NumeroPersone: listaAttesa.NumeroPersone,
    NumeroAlcolico: listaAttesa.NumeroAlcolico,
    NumeroAnalcolico: listaAttesa.NumeroAnalcolico,
    NomiPartecipanti: nomiPartecipanti,
    Note: listaAttesa.Note || "",
  };

  return (
    <BookingModal
      evento={evento}
      isWaitingList={true}
      isAdminView={isAdminEdit}
      onClose={() => navigate(backPath)}
      onSuccess={() => navigate(backPath)}
      editMode={true}
      editId={listaAttesa.ListaAttesaId}
      editType="listaAttesa"
      initialData={initialData}
      waitingListPosition={listaAttesa.Priorita}
    />
  );
};

export default EditWaitingList;
